import React from 'react';

export const severityColors = {
  critical: { bg: 'rgba(248,113,113,0.10)', badge: 'red', dot: '#ef4444', hex: '#ef4444' },
  high: { bg: 'rgba(248,113,113,0.10)', badge: 'red', dot: '#ef4444', hex: '#ef4444' },
  medium: { bg: 'rgba(251,191,36,0.10)', badge: 'amber', dot: '#f59e0b', hex: '#f59e0b' },
  warning: { bg: 'rgba(251,191,36,0.10)', badge: 'amber', dot: '#f59e0b', hex: '#f59e0b' },
  low: { bg: 'rgba(96,165,250,0.10)', badge: 'blue', dot: '#3b82f6', hex: '#3b82f6' },
  info: { bg: 'rgba(96,165,250,0.10)', badge: 'blue', dot: '#3b82f6', hex: '#3b82f6' },
};

export const statusColors = {
  healthy: { badge: 'green', dot: '#22c55e' },
  running: { badge: 'green', dot: '#22c55e' },
  online: { badge: 'green', dot: '#22c55e' },
  stopped: { badge: 'red', dot: '#ef4444' },
  unreachable: { badge: 'red', dot: '#ef4444' },
};

export const ROLE_LEVEL = { viewer: 0, engineer: 1, operator: 2, admin: 3, super_admin: 4 };
export const COLORS = ['#ef4444', '#f59e0b', '#3b82f6', '#10b981', '#8b5cf6', '#06b6d4'];
export const PIE_COLORS = { critical: '#ef4444', high: '#f59e0b', warning: '#3b82f6', info: '#10b981', medium: '#3b82f6', low: '#10b981' };

export function formatBytes(bytes) {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024, sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

export function formatBits(bps) {
  if (!bps || bps === 0) return '0 bps';
  const k = 1000, sizes = ['bps', 'Kbps', 'Mbps', 'Gbps'];
  const i = Math.floor(Math.log(bps) / Math.log(k));
  return parseFloat((bps / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

export function MetricBar({ value, color = '#6366f1' }) {
  const barColor = value > 90 ? '#ef4444' : value > 70 ? '#f59e0b' : color;
  return <div style={{ width: '100%', background: 'var(--glass-strong)', borderRadius: 999, height: 6 }}><div style={{ height: 6, borderRadius: 999, backgroundColor: barColor, transition: 'all 0.5s', width: `${Math.min(value, 100)}%` }} /></div>;
}

export function Gauge({ value = 0, max = 100, size = 80, strokeWidth = 6, label = '', unit = '%' }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const percent = Math.min(value / max, 1);
  const offset = circumference * (1 - percent);
  const color = value > 90 ? '#ef4444' : value > 70 ? '#f59e0b' : value > 50 ? '#6366f1' : '#10b981';
  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="var(--border)" strokeWidth={strokeWidth} />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth={strokeWidth}
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round" transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dashoffset 1s ease' }} />
        <text x={size / 2} y={size / 2} textAnchor="middle" fill="var(--text)"
          fontSize={size > 60 ? 16 : 11} fontWeight="700" fontFamily="Inter, sans-serif" dy="0.35em">
          {Math.round(value)}{unit}
        </text>
      </svg>
      {label && <span style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 4 }}>{label}</span>}
    </div>
  );
}

export function Heatmap({ data = [], days = 30 }) {
  const grid = Array.from({ length: days }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (days - 1 - i));
    const key = d.toISOString().slice(0, 10);
    const entry = data.find(e => e.date?.slice(0, 10) === key);
    return { date: key, count: entry ? (entry.count || entry.total || 0) : 0, day: d.getDay() };
  });
  const maxCount = Math.max(...grid.map(g => g.count), 1);
  function color(c) { const t = c / maxCount; return t === 0 ? 'var(--glass-strong)' : t < 0.25 ? 'rgba(52,211,153,0.35)' : t < 0.5 ? 'rgba(52,211,153,0.65)' : t < 0.75 ? 'rgba(251,191,36,0.7)' : 'rgba(248,113,113,0.75)'; }
  return (
    <div className="flex flex-wrap" style={{ gap: 2 }}>
      {grid.map((g, i) => (
        <div key={i} style={{ width: 11, height: 11, borderRadius: 2, backgroundColor: color(g.count) }}
          title={`${g.date}: ${g.count} incidents`} />
      ))}
    </div>
  );
}

const NODE_STATUS_COLORS = {
  active: '#22c55e', online: '#22c55e', running: '#22c55e',
  degraded: '#eab308', warning: '#eab308',
  offline: '#ef4444', failed: '#ef4444', unknown: '#9ca3af',
};
const statusColor = (s) => NODE_STATUS_COLORS[s] || NODE_STATUS_COLORS.unknown;

export function DependencyGraph({ deps = [] }) {
  if (!deps.length) return <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)', fontSize: 12 }}>No dependencies</div>;
  const half = Math.ceil(deps.length / 2);
  const leftCol = deps.slice(0, half);
  const rightCol = deps.slice(half);
  const Row = ({ dep }) => {
    const sc = statusColor(dep.sourceStatus);
    const tc = statusColor(dep.targetStatus);
    return (
      <div className="flex items-center" style={{ gap: 4, padding: '2px 0', minWidth: 0 }} title={`${dep.from} [${dep.sourceStatus||'?'}] → ${dep.to} [${dep.targetStatus||'?'}]`}>
        <span style={{ flexShrink: 0, width: 8, height: 8, borderRadius: '50%', backgroundColor: sc }} />
        <span style={{ fontSize: 10, color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{dep.from}</span>
        <span style={{ fontSize: 9, color: 'var(--text-3)', flexShrink: 0, margin: '0 2px' }}>→</span>
        <span style={{ flexShrink: 0, width: 8, height: 8, borderRadius: '50%', backgroundColor: tc }} />
        <span style={{ fontSize: 10, color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{dep.to}</span>
      </div>
    );
  };
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 12 }}>
      <div>{leftCol.map((dep, i) => <Row key={i} dep={dep} />)}</div>
      <div>{rightCol.map((dep, i) => <Row key={i + half} dep={dep} />)}</div>
    </div>
  );
}

export function Sparkline({ data = [], dataKey = 'value', color = '#10b981', height = 30 }) {
  if (data.length < 2) return <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)', fontSize: 12 }}>--</div>;
  const vals = data.map(d => d[dataKey] || 0);
  const min = Math.min(...vals), max = Math.max(...vals), range = max - min || 1;
  const points = vals.map((v, i) => `${(i / (vals.length - 1)) * 100},${100 - ((v - min) / range) * 100}`).join(' ');
  return (
    <svg viewBox="0 0 100 100" height={height} style={{ width: '100%' }}>
      <polyline points={points} fill="none" stroke={color} strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
